import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Lang } from "../../i18n";
import { api, qs, uploadFile } from "../client";
import type { Identity, User } from "../types";
import { nativeRuntime, type NativeFile } from "../../platform/native";

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    // /api/auth/me answers null rather than 401 when signed out, so a signed-out visitor
    // produces a successful query with no user instead of an error the UI has to hide.
    queryFn: () => api.get<User | null>("/api/auth/me"),
    staleTime: 60_000,
  });
}

export function useLogin(runtime = nativeRuntime()) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { username: string; password: string; captcha_token?: string }) =>
      runtime?.credentialSignIn(body) ?? api.post<User>("/api/auth/login", body),
    onSuccess: (user) => {
      client.removeQueries({ queryKey: ["development-gate"] });
      client.setQueryData(["me"], user);
      // Role changes what the segment routes return in practice (an editor sees the same
      // rows but can act on them), so anything cached anonymously is now suspect.
      void client.invalidateQueries();
    },
  });
}

export function useLogout() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const runtime = nativeRuntime();
      if (runtime) await runtime.logout();
      else await api.post<void>("/api/auth/logout");
    },
    onSuccess: () => {
      client.removeQueries({ queryKey: ["development-gate"] });
      client.setQueryData(["me"], null);
      void client.invalidateQueries();
    },
  });
}

export interface MobileAttempt {
  purpose: "login" | "account";
  provider: string | null;
  expected_account_id: number | null;
  expires_at: string;
  approved: boolean;
}

export function useMobileAttempt(attemptId: string | null) {
  return useQuery({
    queryKey: ["mobile-attempt", attemptId],
    queryFn: () =>
      api.get<MobileAttempt>(`/api/auth/mobile/attempt${qs({ attempt_id: attemptId })}`),
    enabled: attemptId !== null,
    retry: false,
  });
}

export function useApproveMobileAttempt() {
  return useMutation({
    mutationFn: (attemptId: string) =>
      api.post<{ return_url: string }>("/api/auth/mobile/approve", { attempt_id: attemptId }),
  });
}

export function useRegister() {
  return useMutation({
    // The server answers the same way whether or not the address already has an account,
    // so there is nothing here to write into the cache -- only a message to show.
    mutationFn: (body: {
      username: string;
      email: string;
      password: string;
      language: Lang;
      captcha_token?: string;
    }) => api.post<{ detail: string }>("/api/auth/register", body),
  });
}

export function useConfirmEmail() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (token: string) => api.post<User>("/api/auth/confirm", { token }),
    onSuccess: (user) => {
      // Confirming signs you in, so the cache has to learn that the same way login does.
      client.removeQueries({ queryKey: ["development-gate"] });
      client.setQueryData(["me"], user);
      void client.invalidateQueries();
    },
  });
}

export function useUpdateProfile() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { display_name: string }) => api.put<User>("/api/auth/me/profile", body),
    onSuccess: (user) => client.setQueryData(["me"], user),
  });
}

// A dedicated route rather than a field on `ProfileUpdate`: `PUT /me/profile` is a
// whole-object PUT whose `display_name` defaults to "", so sending a language through it
// would commit whatever half-typed name is in the form.
export function useSaveLanguage() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (language: Lang) => api.put<User>("/api/auth/me/language", { language }),
    onSuccess: (user) => client.setQueryData(["me"], user),
  });
}

export function useUploadAvatar() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (file: File | NativeFile) => uploadFile<User>("/api/auth/me/avatar", file),
    onSuccess: (user) => client.setQueryData(["me"], user),
  });
}

export function useClearAvatar() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.del<User>("/api/auth/me/avatar"),
    onSuccess: (user) => client.setQueryData(["me"], user),
  });
}

/** The providers this account can already sign in with. `enabled` keeps it off signed out. */
export function useIdentities(enabled: boolean) {
  return useQuery({
    queryKey: ["identities"],
    queryFn: () => api.get<Identity[]>("/api/auth/sso/identities"),
    enabled,
    staleTime: 60_000,
  });
}

export function useUnlinkIdentity() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (provider: string) => api.del<void>(`/api/auth/sso/${provider}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["identities"] }),
  });
}

export function useResendConfirmation() {
  return useMutation({
    mutationFn: (email: string) => api.post<{ detail: string }>("/api/auth/resend", { email }),
  });
}

export function useForgotPassword() {
  return useMutation({
    // The server answers the same way whether or not the address has an account, so there is
    // nothing here to write into the cache -- only a message to show.
    mutationFn: (body: { email: string; captcha_token?: string }) =>
      api.post<{ detail: string }>("/api/auth/forgot", body),
  });
}

export function useResetPassword() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { token: string; password: string }) =>
      api.post<User>("/api/auth/reset", body),
    onSuccess: (user) => {
      // A reset signs you in and revokes every session that knew the old password, so the
      // cache has to learn the new one the same way login and confirm teach it.
      client.removeQueries({ queryKey: ["development-gate"] });
      client.setQueryData(["me"], user);
      void client.invalidateQueries();
    },
  });
}

export function useDismissTopic() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (topic: string) => api.put<User>(`/api/auth/me/dismissals/${topic}`, {}),
    onSuccess: (user) => {
      client.setQueryData(["me"], user);
    },
  });
}
